import { Seo } from '@frontend/shared/web';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useFilterStore } from '../../components/filters/filterStore';
import { GlobalFilters } from '../../components/filters/GlobalFilters';
import { CreateIncidentModal } from '../../components/incidents/CreateIncidentModal';
import { IncidentDetailDrawer } from '../../components/incidents/IncidentDetailDrawer';
import { IncidentsFilters } from '../../components/incidents/IncidentsFilters';
import { IncidentsPagination } from '../../components/incidents/IncidentsPagination';
import { IncidentsTable } from '../../components/incidents/IncidentsTable';
import { Header } from '../../components/layout/Header';
import { FEATURES } from '../../constants';
import { useAccount } from '../../contexts/AccountContext';
import { useExtranetIncidentsList } from '../../services/api/hooks/useExtranetIncidentsList';
import type {
  IncidentCategory,
  IncidentPriority,
  IncidentSortDirection,
  IncidentStatus,
} from '../../types/extranet-api.types';

const DEFAULT_LIMIT = 20;
const DEFAULT_SORT_DIRECTION: IncidentSortDirection = 'desc';

export const IncidentsRoute = () => {
  const { can } = useAccount();
  const { scopes, dateRange } = useFilterStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const canCreate = FEATURES.createIncident && can('extranet-incidents');

  const page = Math.max(1, Number(searchParams.get('page') ?? 1));
  const limit = Number(searchParams.get('limit') ?? DEFAULT_LIMIT);
  const status = searchParams.getAll('status') as IncidentStatus[];
  const category = searchParams.getAll('category') as IncidentCategory[];
  const priority = searchParams.getAll('priority') as IncidentPriority[];
  const sortDirection = (searchParams.get('sortDirection') ?? DEFAULT_SORT_DIRECTION) as IncidentSortDirection;

  // Modale création : ouverte par `createIncident=1`, ou pré-remplie par
  // `createForRide=<id>` depuis la table des courses.
  const createForRide = searchParams.get('createForRide') ?? undefined;
  const isCreateOpen = canCreate && (searchParams.get('createIncident') === '1' || Boolean(createForRide));

  const query = useExtranetIncidentsList({
    scopes,
    dateRange,
    page,
    limit,
    status: status.length > 0 ? status : undefined,
    category: category.length > 0 ? category : undefined,
    priority: priority.length > 0 ? priority : undefined,
    sortDirection,
  });

  const items = query.data?.items ?? [];
  const total = query.data?.totalItems ?? items.length;

  const updateParams = useCallback(
    (mutate: (p: URLSearchParams) => void) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      });
    },
    [setSearchParams],
  );

  const setMultiParam = (key: string, values: string[]) =>
    updateParams(p => {
      p.delete(key);
      values.forEach(v => p.append(key, v));
      p.set('page', '1');
    });

  const setSingleParam = (key: string, value: string) =>
    updateParams(p => {
      if (value) p.set(key, value);
      else p.delete(key);
      if (key !== 'page' && key !== 'sortDirection' && key !== 'limit') p.set('page', '1');
    });

  const closeCreate = () =>
    updateParams(p => {
      p.delete('createIncident');
      p.delete('createForRide');
    });

  return (
    <>
      <Seo title="Incidents — Portail extranet" />
      <Header title="Incidents" subtitle="Suivi et traitement des réclamations client" />

      <div className="flex-1 overflow-y-auto">
        <div className="px-8 py-6 flex flex-col gap-5">
          <GlobalFilters permission="extranet-incidents" />

          <div className="flex items-start justify-between gap-3">
            <IncidentsFilters
              status={status}
              onStatusChange={next => setMultiParam('status', next)}
              category={category}
              onCategoryChange={next => setMultiParam('category', next)}
              priority={priority}
              onPriorityChange={next => setMultiParam('priority', next)}
              totalCount={total}
            />
            {canCreate ? (
              <button
                type="button"
                onClick={() => setSingleParam('createIncident', '1')}
                className="h-8 px-3 text-xs font-medium bg-zinc-900 text-white rounded-md hover:bg-zinc-800 transition-colors duration-100 whitespace-nowrap shrink-0"
              >
                + Nouvelle réclamation
              </button>
            ) : null}
          </div>

          <IncidentsTable
            items={items}
            total={total}
            isLoading={query.isLoading}
            isFetching={query.isFetching}
            isError={query.isError}
            error={query.error}
            selectedId={selectedId}
            onRowClick={item => setSelectedId(item.id)}
            sortDirection={sortDirection}
            onSortDirectionChange={next => setSingleParam('sortDirection', next)}
          />

          <IncidentsPagination
            page={page}
            limit={limit}
            total={total}
            onPageChange={p => setSingleParam('page', String(p))}
            onLimitChange={l =>
              updateParams(p => {
                p.set('limit', String(l));
                p.set('page', '1');
              })
            }
          />
        </div>
      </div>

      <IncidentDetailDrawer incidentId={selectedId} onClose={() => setSelectedId(null)} />

      {isCreateOpen ? <CreateIncidentModal initialCourseId={createForRide} onClose={closeCreate} /> : null}
    </>
  );
};
